import { ContractNegotiation } from '../../models/Entities/Contracts/ContractNegotiation';
import { NewContractNegotiation } from '../../models/Entities/Contracts/NewContractNegotiation';
import { ErrorType } from '../../models/HttpError';
import * as Services from '../../services/ContractNegotiation.Services';
import * as Actions from '../actions';
import { AppThunkAction } from '../reducers';

type KnownAction =
  | Actions.ContractNegotiation.ContractNegotiation_GetAllBycontractId_Request_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetAllBycontractId_Receive_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetAllBycontractId_Error_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetAllByAddendumId_Request_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetAllByAddendumId_Receive_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetAllByAddendumId_Error_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetById_Request_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetById_Receive_Action
  | Actions.ContractNegotiation.ContractNegotiation_GetById_Error_Action
  | Actions.ContractNegotiation.ContractNegotiation_Create_Request_Action
  | Actions.ContractNegotiation.ContractNegotiation_Create_Receive_Action
  | Actions.ContractNegotiation.ContractNegotiation_Create_Error_Action
  | Actions.ContractNegotiation.ContractNegotiation_Update_Request_Action
  | Actions.ContractNegotiation.ContractNegotiation_Update_Receive_Action
  | Actions.ContractNegotiation.ContractNegotiation_Update_Error_Action
  | Actions.ContractNegotiation.ContractNegotiation_Delete_Request_Action
  | Actions.ContractNegotiation.ContractNegotiation_Delete_Receive_Action
  | Actions.ContractNegotiation.ContractNegotiation_Delete_Error_Action;

const GetUnauthorizedError = (): ErrorType => ({
  ErrorCode: 401,
  ErrorMessage: 'Not authorized',
  Errors: [],
});

export const actionCreators = {
  GetContractNegotiations:
    (contractId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({
          type: 'CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_ERROR_ACTION',
          contractId,
          error: GetUnauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_REQUEST_ACTION',
        contractId,
      });

      Services.GetContractNegotiations(contractId, token)
        .then((contractNegotiations: ContractNegotiation[]) => {
          dispatch({
            type: 'CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_RECEIVE_ACTION',
            contractNegotiations,
            contractId,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONTRACTNEGOTIATION_GET_ALL_BY_CONTRACTID_ERROR_ACTION',
            contractId,
            error,
          })
        );
    },
  GetContractNegotiationsByAddendumId:
    (contractId: number, addendumId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({
          type: 'CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_ERROR_ACTION',
          contractId,
          addendumId,
          error: GetUnauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_REQUEST_ACTION',
        contractId,
        addendumId,
      });

      Services.GetContractNegotiationsByAddendumId(addendumId, token)
        .then((contractNegotiations: ContractNegotiation[]) => {
          dispatch({
            type: 'CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_RECEIVE_ACTION',
            contractNegotiations,
            contractId,
            addendumId,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONTRACTNEGOTIATION_GET_ALL_BY_ADDENDUMID_ERROR_ACTION',
            contractId,
            addendumId,
            error,
          })
        );
    },
  GetContractNegotiationById:
    (id: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({
          type: 'CONTRACTNEGOTIATION_GET_BY_ID_ERROR_ACTION',
          id,
          error: GetUnauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONTRACTNEGOTIATION_GET_BY_ID_REQUEST_ACTION',
        id,
      });

      Services.GetContractNegotiationById(id, token)
        .then((contractNegotiation: ContractNegotiation) => {
          dispatch({
            type: 'CONTRACTNEGOTIATION_GET_BY_ID_RECEIVE_ACTION',
            contractNegotiation,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONTRACTNEGOTIATION_GET_BY_ID_ERROR_ACTION',
            id,
            error,
          })
        );
    },
  CreateContractNegotiation:
    (contractNegotiation: NewContractNegotiation): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({
          type: 'CONTRACTNEGOTIATION_CREATE_ERROR_ACTION',
          contractNegotiation,
          error: GetUnauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONTRACTNEGOTIATION_CREATE_REQUEST_ACTION',
        contractNegotiation,
      });

      Services.CreateContractNegotiation(contractNegotiation, token)
        .then((savedContractNegotiation: ContractNegotiation) => {
          dispatch({
            type: 'CONTRACTNEGOTIATION_CREATE_RECEIVE_ACTION',
            contractNegotiation: savedContractNegotiation,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONTRACTNEGOTIATION_CREATE_ERROR_ACTION',
            contractNegotiation,
            error,
          })
        );
    },
  UpdateContractNegotiation:
    (contractNegotiation: ContractNegotiation): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({
          type: 'CONTRACTNEGOTIATION_UPDATE_ERROR_ACTION',
          contractNegotiation,
          error: GetUnauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONTRACTNEGOTIATION_UPDATE_REQUEST_ACTION',
        contractNegotiation,
      });

      Services.UpdateContractNegotiation(contractNegotiation, token)
        .then((updatedContractNegotiation: ContractNegotiation) => {
          dispatch({
            type: 'CONTRACTNEGOTIATION_UPDATE_RECEIVE_ACTION',
            contractNegotiation: updatedContractNegotiation,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONTRACTNEGOTIATION_UPDATE_ERROR_ACTION',
            contractNegotiation,
            error,
          })
        );
    },
  DeleteContractNegotiation:
    (id: string, contractId: number, contractAddendumId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const token = state.oidc?.user?.access_token;

      if (!token) {
        dispatch({
          type: 'CONTRACTNEGOTIATION_DELETE_ERROR_ACTION',
          id,
          contractId,
          contractAddendumId,
          error: GetUnauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONTRACTNEGOTIATION_DELETE_REQUEST_ACTION',
        id,
        contractId,
        contractAddendumId,
      });

      Services.DeleteContractNegotiation(id, token)
        .then(() => {
          dispatch({
            type: 'CONTRACTNEGOTIATION_DELETE_RECEIVE_ACTION',
            id,
            contractId,
            contractAddendumId,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONTRACTNEGOTIATION_DELETE_ERROR_ACTION',
            id,
            contractId,
            contractAddendumId,
            error,
          })
        );
    },
};